/**
 * Grafik qatlami — recharts ustidagi umumiy qism.
 *
 * NIMA UCHUN ALOHIDA FAYL
 * Loyihada uch joyda uch xil grafik uslubi bor edi: `TrendChart` tokenlarni
 * `getComputedStyle` bilan o'qib, o'z ko'rsatkichi va afsonasi bilan; boshqa
 * ikkitasi esa recharts ning standart tooltipini `contentStyle` bilan bo'yab.
 * Natijada bir sahifadan ikkinchisiga o'tganda grafik boshqa tilda gapirardi.
 * Endi umumiy qism shu yerda, har grafik shundan quriladi.
 *
 * NIMA UCHUN KIT ICHIDA EMAS (`src/ui/` da emas)
 * Kit ataylab bog'liqliksiz: `react` va `react-icons`, xolos — shuning uchun
 * uni boshqa loyihaga papka sifatida ko'chirish mumkin. `recharts` ni kitga
 * kiritish shu xususiyatni buzardi. Grafik esa loyihaning o'z qatlami:
 * `src/components/`. Kitdan faqat tokenlar va sinflar olinadi.
 *
 * YANGI KUTUBXONA QO'SHILMAYDI: recharts 2.13.3 allaqachon bog'liqlikda.
 */

import { useEffect, useState } from 'react'
import { ResponsiveContainer } from 'recharts'

/* ─────────────────────────────────────────────────────────────
   Tokenlarni o'qish
   ───────────────────────────────────────────────────────────── */

// Recharts SVG atributlarga (stroke, fill) haqiqiy rang kutadi — `var(--ui-*)`
// u yerda ishlamaydi. Shuning uchun tokenlarni ildiz elementdan o'qib olamiz va
// mavzu almashganda (data-theme yoki tizim sozlamasi) qayta o'qiymiz.
const TOKENS = [
  '--ui-text-1',
  '--ui-text-2',
  '--ui-text-3',
  '--ui-border',
  '--ui-border-2',
  '--ui-surface',
  '--ui-surface-2',
  '--ui-st-ok',
  '--ui-st-critical',
  '--ui-st-medium',
  '--ui-st-high',
]

function readTokens() {
  const cs = getComputedStyle(document.documentElement)
  const out = {}
  for (const name of TOKENS) out[name] = cs.getPropertyValue(name).trim()
  return out
}

/**
 * Joriy mavzudagi token qiymatlari. Mavzu almashganda o'zi yangilanadi,
 * shuning uchun grafik yorug' va tungi mavzuda bir xil to'g'ri ko'rinadi.
 */
export function useThemeTokens() {
  const [tokens, setTokens] = useState(readTokens)

  useEffect(() => {
    const sync = () => setTokens(readTokens())
    sync()
    // data-theme atributi App.jsx da ildizga qo'yiladi
    const mo = new MutationObserver(sync)
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    // Atribut umuman qo'yilmagan holat — tizim mavzusiga ergashamiz
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    mq.addEventListener('change', sync)
    return () => { mo.disconnect(); mq.removeEventListener('change', sync) }
  }, [])

  return tokens
}

/**
 * Seriya ohangi → token rangi.
 *
 * Grafik uchun ALOHIDA PALITRA O'YLAB TOPILMAYDI (DESIGN.md 4): siyoh —
 * hajm (o'zi na yaxshi, na yomon), `ok` — bajarilgan ish, `critical` —
 * nuqson, `medium` — e'tibor kerak, `muted` — ikkilamchi seriya.
 * Seriyalar rangdan tashqari YORUG'LIK bo'yicha ham ajraladi.
 */
export function toneColor(tokens, tone) {
  switch (tone) {
    case 'ok': return tokens['--ui-st-ok']
    case 'critical': return tokens['--ui-st-critical']
    case 'medium': return tokens['--ui-st-medium']
    case 'high': return tokens['--ui-st-high']
    case 'muted': return tokens['--ui-text-3']
    case 'ink':
    default: return tokens['--ui-text-1']
  }
}

/**
 * X o'qidagi belgilar oralig'i.
 *
 * Ilgari har kun uchun belgi chizilardi: yozuvlar bir-biriga yelimlanib,
 * recharts esa ularning bir qismini o'zi tashlab ketardi — natijada qadam
 * notekis chiqardi (04.07, keyin 06.07–07.07, keyin bo'shliq, keyin 01.08).
 * Notekis qadam grafikni yolg'on o'qitadi: ko'z bir xil oraliqni kutadi.
 * Shuning uchun qadam ATAYLAB belgilanadi — kunlik oynada har kun, oylikda
 * haftada bir, choraklikda ikki haftada bir.
 */
export function chartTickInterval(len) {
  if (len <= 10) return 0        // har nuqta
  if (len <= 31) return 6        // haftada bir
  return 13                      // ikki haftada bir
}

/* ─────────────────────────────────────────────────────────────
   Ko'rsatkich (tooltip) va afsona (legend)
   ───────────────────────────────────────────────────────────── */

/**
 * Grafik ko'rsatkichi — recharts niki emas, o'zimizniki.
 * Standart tooltip mavzuni bilmaydi va har sahifada qaytadan bo'yaladi.
 *
 * @param valueFmt  qiymatni matnga aylantiruvchi: v => `$${v}`
 */
export function ChartTooltip({ active, payload, label, valueFmt }) {
  if (!active || !payload?.length) return null
  const fmt = valueFmt || ((v) => v)
  return (
    <div className="ui-chart__tip">
      <div className="ui-chart__tip-hd">{label}</div>
      {payload.map((p) => (
        <div key={p.name} className="ui-chart__tip-row">
          <span className="ui-chart__tip-mark" aria-hidden="true" style={{ background: p.color }} />
          <span className="ui-grow">{p.name}</span>
          <span className="ui-chart__tip-val">{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * Grafik afsonasi.
 *
 * Sabab: recharts afsonasi belgini `--ui-fs-xs` dan kichik chizadi va rang
 * namunasini kvadrat qilib beradi; bu yerda esa namuna CHIZIQ bo'lishi kerak —
 * grafikda ko'ringan narsaning aynan o'zi. `variant="area"` ustunli grafik
 * uchun: u yerda namuna ham to'ldirilgan bo'lak.
 */
export function ChartLegend({ series = [], variant = 'line' }) {
  if (!series.length) return null
  return (
    <div className="ui-chart__legend">
      {series.map((s) => (
        <span key={s.name} className="ui-chart__leg">
          <span
            aria-hidden="true"
            className={`ui-chart__leg-mark${variant === 'area' ? ' ui-chart__leg-mark--area' : ''}`}
            style={{ background: s.color }}
          />
          {s.name}
        </span>
      ))}
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────
   ChartFrame
   ───────────────────────────────────────────────────────────── */

/**
 * Grafik ramkasi: sarlavha, amallar, afsona, balandlik va bo'sh holat.
 *
 * Ichida recharts daraxti turadi — ramka uni `ResponsiveContainer` ga
 * o'raydi, ya'ni har grafikda buni qayta yozish shart emas. Bo'sh holat
 * ham shu yerda: ma'lumot yo'q bo'lganda bo'sh o'q chizmaymiz, matn beramiz
 * (bo'sh o'q "nol" degan yolg'on xabar beradi).
 *
 * @param title    sarlavha (ixtiyoriy)
 * @param sub      sarlavha ostidagi izoh — masalan davr bog'lanishi
 * @param actions  o'ng tarafdagi boshqaruv (davr tanlagichi va h.k.)
 * @param legend   [{name, color}] — `toneColor()` bilan tayyorlanadi
 * @param legendVariant  'line' (standart) | 'area'
 * @param height   grafik balandligi, px (standart 220)
 * @param empty    ma'lumot yo'qmi (standart — bolalar yo'qligi bilan aniqlanmaydi)
 * @param emptyText  bo'sh holat matni
 */
export function ChartFrame({
  title,
  sub,
  actions,
  legend = [],
  legendVariant = 'line',
  height = 220,
  empty = false,
  emptyText = "Grafik uchun ma'lumot yo'q",
  className,
  children,
  ...rest
}) {
  return (
    <div className={['ui-chart', className].filter(Boolean).join(' ')} {...rest}>
      {(title || actions || sub) && (
        <div className="ui-chart__hdr">
          <div className="ui-col">
            {title && <div className="ui-chart__title">{title}</div>}
            {sub && <div className="ui-chart__sub">{sub}</div>}
          </div>
          <span className="ui-grow" />
          {actions}
        </div>
      )}

      {!empty && <ChartLegend series={legend} variant={legendVariant} />}

      {empty ? (
        <div className="ui-chart__empty" style={{ height }}>{emptyText}</div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          {children}
        </ResponsiveContainer>
      )}
    </div>
  )
}
